import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import Checkbox from '@mui/material/Checkbox'
import Tooltip from '@mui/material/Tooltip'
import { styled } from '@mui/material/styles'
import {
  DataGrid as DataGridMUI,
  GridToolbarContainer,
  GridActionsCellItem,
  GridRowModes,
  useGridApiContext,
} from '@mui/x-data-grid'
import { v4 as uuidv4 } from 'uuid'
import { Stack } from '@mui/material'
import AppContext from '../../../context/appContext'

const StyledBox = styled('div')(({ theme }) => ({
  width: '100%',
  '& .MuiDataGrid-cell--editing': {
    backgroundColor: 'rgb(255,215,115, 0.19)',
    color: '#1a3e72',
    '& .MuiInputBase-root': { height: '100%' },
  },
  '& .Mui-error': {
    backgroundColor: 'rgb(126,10,15, 0.1)',
    color: theme.palette.error.main,
    ...theme.applyStyles('dark', { backgroundColor: 'rgb(126,10,15, 0)' }),
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    textAlign: 'center', // Center align the text
    whiteSpace: 'pre-line', // Ensure line breaks are respected
  },
  '& .error-cell': {
    backgroundColor: 'rgba(255, 0, 0, 0.1) !important',
    border: '1px solid red !important',
  },
  marginBottom: '2vh',
}))

const EditToolbar = ({ addButtonCaption, fieldToFocus, setRows, setPartialChanges }) => {
  const apiRef = useGridApiContext()

  const handleClick = () => {
    const id = uuidv4()
    const newRow = { id, isNew: true }

    setRows(oldRows => [...oldRows, newRow])
    setPartialChanges(true)

    apiRef.current.startRowEditMode({ id, fieldToFocus })
  }

  return (
    <GridToolbarContainer>
      <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
        {addButtonCaption}
      </Button>
    </GridToolbarContainer>
  )
}

const OptionalColumnHeader = ({ column, onOptionalChange, hasData, isOptional, readOnly }) => {
  const handleChange = event => {
    const newValue = event.target.checked
    onOptionalChange(column.field, newValue)
  }

  const hasExistingData = hasData && !isOptional
  const tooltipTitle = hasExistingData ? 'Datos existentes' : 'Omitir'

  return (
    <Stack direction="column" gap={0} justifyContent="center">
      <Tooltip title={tooltipTitle}>
        <span>
          <Checkbox
            checked={!!isOptional}
            disabled={readOnly || hasExistingData}
            size="small"
            sx={{ padding: 0 }}
            onChange={handleChange}
            onClick={e => e.stopPropagation()}
          />
        </span>
      </Tooltip>
      <span>{column.headerName}</span>
    </Stack>
  )
}

const DataGrid = ({
  title,
  subtitle,
  addButtonCaption,
  setIsEditingMode,
  columns,
  rows,
  setRows,
  optionalColumns,
  setOptionalColumns,
  readOnly = false,
  ...props
}) => {
  const { setPartialChanges } = useContext(AppContext)
  const [rowModesModel, setRowModesModel] = useState({})

  const handleOptionalColumnChange = useCallback(
    (field, isOptional) => {
      setOptionalColumns(prev => ({
        ...prev,
        [field]: isOptional,
      }))
      setPartialChanges(true)
    },
    [setPartialChanges, setOptionalColumns],
  )

  const handleDeleteClick = useCallback(
    id => async () => {
      setPartialChanges(true)
      const newRows = rows
        .filter(row => row.id !== id || (row.id === id && !row.isNew))
        .map(row => (!row.isNew && row.id === id ? { ...row, isDeleted: true } : { ...row }))
      setRows(newRows)

      setRowModesModel(prevModel => {
        const newModel = { ...prevModel }
        delete newModel[id]
        return newModel
      })
    },
    [rows, setPartialChanges, setRows],
  )

  useEffect(() => {
    const isARowInEditMode = Object.values(rowModesModel).some(
      model => model.mode === GridRowModes.Edit,
    )
    setIsEditingMode(isARowInEditMode)
  }, [rowModesModel, setIsEditingMode])

  const actionsColumn = useMemo(
    () => ({
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }) => [
        <GridActionsCellItem
          key={`delete-${id}`}
          color="inherit"
          icon={<DeleteIcon />}
          label="Delete"
          onClick={handleDeleteClick(id)}
        />,
      ],
    }),
    [handleDeleteClick],
  )

  const columnsWithActions = useMemo(
    () => [
      ...columns.map(column => {
        const baseColumn = column.required
          ? { ...column, headerName: `${column.headerName} *` }
          : { ...column }

        if (column.allowOptional) {
          const isOptional = optionalColumns[column.field]
          const hasData = rows.some(row => {
            const value = row[column.field]
            return value !== null && value !== undefined && value !== ''
          })

          return {
            ...baseColumn,
            renderHeader: () => (
              <OptionalColumnHeader
                column={baseColumn}
                hasData={hasData}
                isOptional={isOptional}
                readOnly={readOnly}
                onOptionalChange={handleOptionalColumnChange}
              />
            ),
            validate: params => {
              if (isOptional) {
                return null
              }
              return column.validate ? column.validate(params) : null
            },
            renderCell: params => {
              if (isOptional) {
                return (
                  <div
                    style={{ background: 'rgba(224, 224, 224, 1)', width: '100%', height: '100%' }}
                  >
                    {params.value ?? null}
                  </div>
                )
              }
              return column.renderCell ? column.renderCell(params) : params.value || ''
            },
            editable: !isOptional && baseColumn.editable,
          }
        }
        return baseColumn
      }),
      ...(readOnly ? [] : [actionsColumn]),
    ],
    [columns, rows, actionsColumn, optionalColumns, handleOptionalColumnChange, readOnly],
  )

  const processRowUpdate = newRow => {
    setPartialChanges(true)
    const { errors, ...updateRowWithoutErrors } = newRow
    setRows(oldRows => oldRows.map(row => (row.id === newRow.id ? updateRowWithoutErrors : row)))
    return updateRowWithoutErrors
  }

  const handleRowClick = useCallback(params => {
    setRowModesModel(prevModel => ({
      ...Object.keys(prevModel).reduce(
        (acc, id) => ({ ...acc, [id]: { mode: GridRowModes.View } }),
        {},
      ),
      [params.id]: { mode: GridRowModes.Edit },
    }))
  }, [])

  const handleRowModesModelChange = useCallback(newModel => {
    setRowModesModel(newModel)
  }, [])

  const filteredRows = useMemo(() => rows?.filter(row => !row.isDeleted), [rows])

  return (
    <StyledBox>
      {title && (
        <Typography sx={{ fontSize: 20, fontWeight: 600, textAlign: 'left', marginBottom: '10px' }}>
          {title}
        </Typography>
      )}
      {subtitle && (
        <Typography sx={{ fontSize: 14, fontWeight: 400, textAlign: 'left', marginBottom: '10px' }}>
          {subtitle}
        </Typography>
      )}
      <DataGridMUI
        disableColumnMenu
        columns={columnsWithActions}
        editMode="row"
        getCellClassName={params => (params.row?.errors?.[params?.field] ? 'error-cell' : '')}
        getRowId={row => row.id}
        isCellEditable={readOnly ? () => false : undefined}
        localeText={{ noRowsLabel: '' }}
        processRowUpdate={processRowUpdate}
        rowModesModel={rowModesModel}
        rows={filteredRows}
        slotProps={{
          toolbar: {
            addButtonCaption,
            fieldToFocus: columns?.length ? columns[0].field : '',
            setPartialChanges,
            setRows,
          },
        }}
        slots={readOnly ? {} : { toolbar: EditToolbar }}
        onRowClick={readOnly ? undefined : handleRowClick}
        onRowModesModelChange={handleRowModesModelChange}
        {...props}
      />
    </StyledBox>
  )
}

export default DataGrid
